import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Download, Plus, Trash2, ArrowLeft, Sparkles, RotateCcw, Building2 , Save, Clock , FileText, FileUp, FileDown} from "lucide-react";
import { useAuth, useSettings } from "../App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StakeholderCommunicationLogForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, activeSettings } = useSettings();
  const [selectedSetting, setSelectedSetting] = useState(activeSettings?.id || "none");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: "",
    date: new Date().toISOString().split("T")[0],
    communications: [{ date: "", stakeholder: "", channel: "", topic: "", summary: "", follow_up: "", owner: "", status: "Open" }]
  });

  const getSampleData = () => ({
    project_name: "CRM Implementation Project",
    date: new Date().toISOString().split("T")[0],
    communications: [
      { date: "2025-01-20", stakeholder: "John Smith (CEO)", channel: "Email", topic: "Project Status Update", summary: "Provided executive summary of Q1 progress. CEO satisfied with milestones achieved.", follow_up: "Send detailed budget report by EOW", owner: "Project Manager", status: "In Progress" },
      { date: "2025-01-18", stakeholder: "IT Department", channel: "Meeting", topic: "Technical Requirements", summary: "Discussed integration requirements with legacy systems. Need additional APIs.", follow_up: "Schedule technical deep-dive session", owner: "Tech Lead", status: "Open" },
      { date: "2025-01-15", stakeholder: "Finance Team", channel: "Teams Call", topic: "Budget Approval", summary: "Phase 2 budget approved with minor adjustments. Contingency reduced to 8%.", follow_up: "Update project budget documentation", owner: "Finance Lead", status: "Completed" },
      { date: "2025-01-12", stakeholder: "End Users", channel: "Survey", topic: "Requirements Gathering", summary: "Collected feedback from 50+ users. Top requests: mobile access, custom reports.", follow_up: "Prioritize feature requests in backlog", owner: "BA Team", status: "Completed" }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Stakeholder Communication Log Draft";
      const payload = {
        name: draftName,
        template_type: "Stakeholder Communication Log",
        template_id: "stakeholder-communication-log",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  useEffect(() => {
    if (activeSettings?.id) setSelectedSetting(activeSettings.id);
  }, [activeSettings]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "stakeholder-communication-log", "Stakeholder Communication Log", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("stakeholder-communication-log", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      let url = `${API}/generate/stakeholder-communication-log`;
      if (selectedSetting && selectedSetting !== "none") {
        url += `?settings_id=${selectedSetting}`;
      }
      const response = await axios.post(url, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Stakeholder_Communication_Log_${formData.project_name || "Document"}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Stakeholder Communication Log",
        formData,
        settings: activeSettings || {},
        templateType: "Stakeholder Communication Log",
        templateId: "generate",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const updateCommunication = (index, field, value) => {
    const updated = [...formData.communications];
    updated[index][field] = value;
    setFormData({ ...formData, communications: updated });
  };

  const addCommunication = () => {
    setFormData({
      ...formData,
      communications: [...formData.communications, { date: "", stakeholder: "", channel: "", topic: "", summary: "", follow_up: "", owner: "", status: "Open" }]
    });
  };

  const removeCommunication = (index) => {
    setFormData({
      ...formData,
      communications: formData.communications.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Stakeholder Communication Log</h1>
                <p className="text-sm text-gray-500">Track all stakeholder communications</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {settings.length > 0 && (
                <Select value={selectedSetting} onValueChange={setSelectedSetting}>
                  <SelectTrigger className="w-[180px]">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Header/Footer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Header/Footer</SelectItem>
                    {settings.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.is_default && "★"}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn">
                <Sparkles className="h-4 w-4 mr-2" /> Sample
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset
              </Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Communication Log Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project Name *</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({ ...formData, project_name: e.target.value })} placeholder="Project name" data-testid="project-name-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="date-input" />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <Label className="text-lg font-semibold">Communications</Label>
              {formData.communications.map((comm, index) => (
                <Card key={index} className="p-4 bg-gray-50">
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={comm.date} onChange={(e) => updateCommunication(index, "date", e.target.value)} />
                    </div>
                    <div>
                      <Label>Stakeholder</Label>
                      <Input value={comm.stakeholder} onChange={(e) => updateCommunication(index, "stakeholder", e.target.value)} placeholder="Name (Role)" />
                    </div>
                    <div>
                      <Label>Channel</Label>
                      <Select value={comm.channel} onValueChange={(v) => updateCommunication(index, "channel", v)}>
                        <SelectTrigger><SelectValue placeholder="Select channel" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Email">Email</SelectItem>
                          <SelectItem value="Meeting">Meeting</SelectItem>
                          <SelectItem value="Teams Call">Teams Call</SelectItem>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Survey">Survey</SelectItem>
                          <SelectItem value="Slack">Slack</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Status</Label>
                      <Select value={comm.status} onValueChange={(v) => updateCommunication(index, "status", v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Open">Open</SelectItem>
                          <SelectItem value="In Progress">In Progress</SelectItem>
                          <SelectItem value="Completed">Completed</SelectItem>
                          <SelectItem value="Pending Response">Pending Response</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label>Topic</Label>
                      <Input value={comm.topic} onChange={(e) => updateCommunication(index, "topic", e.target.value)} placeholder="Communication topic" />
                    </div>
                    <div>
                      <Label>Owner</Label>
                      <Input value={comm.owner} onChange={(e) => updateCommunication(index, "owner", e.target.value)} placeholder="Responsible person" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Summary</Label>
                      <Input value={comm.summary} onChange={(e) => updateCommunication(index, "summary", e.target.value)} placeholder="Brief summary" />
                    </div>
                    <div>
                      <Label>Follow-up Required</Label>
                      <Input value={comm.follow_up} onChange={(e) => updateCommunication(index, "follow_up", e.target.value)} placeholder="Next steps" />
                    </div>
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button variant="ghost" size="sm" onClick={() => removeCommunication(index)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </Card>
              ))}
              <Button variant="outline" onClick={addCommunication}>
                <Plus className="h-4 w-4 mr-2" /> Add Communication
              </Button>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default StakeholderCommunicationLogForm;
